import { useNavigate } from '@tanstack/react-router';

import { CreateOutreachModal } from '@apps/web/components/outreaches/create_outreach_modal';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { useMode } from '@apps/web/lib/mode_store';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

/** Carries map selections through the existing composer and start endpoint. */
export function MapManualOutreachModal({
    organizationId,
    shiftIds,
    caregiverIds,
    onClose,
}: {
    readonly organizationId: string;
    readonly shiftIds: Array<string>;
    readonly caregiverIds: ReadonlyArray<string>;
    readonly onClose: () => void;
}) {
    const mode = useMode() ?? 'live';
    const navigate = useNavigate();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'coverage-manual-outreach',
    });

    return (
        <CreateOutreachModal
            isOpen={true}
            onClose={onClose}
            shiftIds={shiftIds}
            navigateToOutreachOnManualSuccess={false}
            manualEntry={{
                title: 'Start Outreach',
                description: `Manual outreach · ${caregiverIds.length} caregivers selected from the map. Continue starts outreach to these caregivers.`,
                submitLabel: 'Continue',
                submittingLabel: 'Starting…',
                validateSelection: (selectedShiftIds) => {
                    if (
                        !organization?.callout_settings?.manual_outreach_enabled
                    ) {
                        return 'Manual outreach is not enabled for this organization.';
                    }
                    if (
                        caregiverIds.length === 0 ||
                        (mode !== 'sandbox' &&
                            !organization?.features?.[
                                'outreach_sending_enabled'
                            ])
                    ) {
                        return 'Select caregivers and enable outreach sending before continuing.';
                    }
                    if (
                        selectedShiftIds.length !== shiftIds.length ||
                        selectedShiftIds.some((id) => !shiftIds.includes(id))
                    ) {
                        return 'Return to the map to choose caregivers for a different shift.';
                    }
                    return undefined;
                },
                afterCreate: async (outreachId) => {
                    // The draft is durable. Resolve failures here and open it
                    // for recovery instead of letting the composer create twice.
                    try {
                        const started = await getApiClient().post({
                            url: '/organizations/{organization_id}/outreaches/{outreach_id}/start-outreach',
                            pathParams: {
                                organization_id: organizationId,
                                outreach_id: outreachId,
                            },
                            mode,
                            body: {
                                caregiver_shift_ids: Object.fromEntries(
                                    caregiverIds.map((id) => [id, shiftIds]),
                                ),
                                skip_conflict_check: false,
                                suppress_agent_notification: false,
                            },
                            reason: 'coverage-manual-outreach',
                        });
                        const skippedCount = new Set([
                            ...(started.dropped_inactive_caregiver_ids ?? []),
                            ...(started.dropped_no_valid_shifts_caregiver_ids ??
                                []),
                            ...(started.skipped_staged_caregivers ?? []).map(
                                (item) => item.caregiver_id,
                            ),
                        ]).size;
                        if (skippedCount > 0) {
                            toast.error(
                                `${skippedCount} selected caregivers were skipped. Review the outreach details before continuing.`,
                                {
                                    persistNotification: true,
                                    notificationType: 'outreach_action',
                                },
                            );
                        } else {
                            toast.success('Outreach started', {
                                persistNotification: true,
                                notificationType: 'outreach_action',
                            });
                        }
                    } catch {
                        toast.error(
                            'Outreach was created, but its start could not be confirmed. Review its details before trying again.',
                            {
                                persistNotification: true,
                                notificationType: 'outreach_action',
                            },
                        );
                    }
                    void navigate({
                        to: '/outreaches/$callout-id',
                        params: { 'callout-id': outreachId },
                        search: { openChat: true, organizationId, mode },
                    });
                },
            }}
        />
    );
}
